import type { SupabaseClient } from '@supabase/supabase-js';
import { getGlobalLatticePrices, getBatchGlobalLatticePrices } from '../../services/supabaseService';
import {
  GroundedDeal,
  GroundedProduct,
  StructuredProductName,
  structureProductTitles,
  suggestCategory,
  groundMissingIdentity
} from '../../services/geminiService';
import { destructureName, formatDisplayName, computePricePerUnit } from './intelligence';
import { getFormattedStoreDisplayName } from '../../utils/storeUtils';
import { CALIBRATION_DEFAULTS } from './calibration';

interface ResolveMarketDealsParams {
  supabase: SupabaseClient;
  query: string;
  itemPrice?: number | null;
  itemSize?: number | string | null;
  itemUnit?: string | null;
  userLat?: number | null;
  userLng?: number | null;
  userAddress?: string | null;
  radiusMiles?: number;
  lookbackHours?: number;
}

// Plausibility bands for Tier 2/3 Serper estimates against the scanned item.
// Same-unit sizes are compared directly (catches "42 ct vs 152 ct" mismatches).
// When size can't be compared (missing or mismatched units — full cross-unit
// conversion is a separate, not-yet-built feature), price ratio is the fallback
// sanity check so a wildly different pack size can't masquerade as a comparable deal.
const SIZE_RATIO_BAND: [number, number] = [0.5, 2.0];
const PRICE_RATIO_BAND: [number, number] = [0.35, 2.5];

function isPlausibleMatch(
  dealPrice: number,
  dealSize: number | string | null,
  dealUnit: string | null,
  itemPrice?: number | null,
  itemSize?: number | string | null,
  itemUnit?: string | null
): boolean {
  const numericDealSize = typeof dealSize === 'string' ? parseFloat(dealSize) : dealSize;
  const numericItemSize = typeof itemSize === 'string' ? parseFloat(itemSize) : itemSize;

  const sizeComparable =
    numericDealSize && numericItemSize && dealUnit && itemUnit &&
    dealUnit.trim().toLowerCase() === itemUnit.trim().toLowerCase();

  // A matching pack size does not excuse an implausible price — e.g. a 12oz box
  // priced at 12x a 13.5oz box's price is still wrong, whether that's a mislabeled
  // multi-pack, a parsing error, or bad source data. Both checks apply independently;
  // either one failing rejects the match. Size alone is no longer allowed to bypass
  // the price check the way it previously did.
  if (sizeComparable) {
    const sizeRatio = numericDealSize / numericItemSize;
    if (sizeRatio < SIZE_RATIO_BAND[0] || sizeRatio > SIZE_RATIO_BAND[1]) return false;
  }

  if (itemPrice && itemPrice > 0) {
    const priceRatio = dealPrice / itemPrice;
    if (priceRatio < PRICE_RATIO_BAND[0] || priceRatio > PRICE_RATIO_BAND[1]) return false;
  }

  // No reference price or size to sanity-check against — can't rule it implausible.
  return true;
}

// Below this many Tier 1 (GPS-verified receipt) results nearby, supplement with
// Serper Tier 2/3 regional estimates rather than relying on Lattice data alone.
const MIN_LOCAL_LATTICE_DENSITY = 3;

async function getMarketDiscoverToken(): Promise<string | null> {
  try {
    if ((window as any).Clerk?.session) {
      return await (window as any).Clerk.session.getToken({ template: 'supabase' });
    }
  } catch (e) {
    console.warn('[marketDiscovery] Failed to get Clerk token:', e);
  }
  return null;
}

interface RawMarketDeal {
  title: string;
  imageUrl: string | null;
  sourceUrl: string | null;
  price: number;
  storeName: string;
  // Clean brand name Serper itself extracted (e.g. "Walmart") — better logo-lookup
  // material than storeName, which is a specific branch title (e.g. "Walmart
  // Supercenter #1234"). Falls back to storeName when absent.
  chainName: string | null;
  storeAddress: string | null;
  branchLat: number | null;
  branchLng: number | null;
  distanceMiles: number | null;
}

/** Thrown (only when requested) when the price-search provider is down, e.g. out of Serper credits. */
export class MarketDiscoveryUnavailableError extends Error {
  constructor(public reason: string) {
    super(`Live Market Discovery is unavailable: ${reason}`);
    this.name = 'MarketDiscoveryUnavailableError';
  }
}

async function callMarketDiscoverApi(
  query: string,
  lat: number,
  lng: number,
  address?: string | null,
  throwIfUnavailable = false
): Promise<RawMarketDeal[]> {
  const token = await getMarketDiscoverToken();
  const response = await fetch('/api/market/discover', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ query, lat, lng, address: address || undefined }),
  });
  if (!response.ok) {
    if (throwIfUnavailable && response.status === 503) {
      const body = await response.json().catch(() => ({}));
      throw new MarketDiscoveryUnavailableError(body?.reason || body?.error || 'service unavailable');
    }
    return [];
  }
  const { deals } = await response.json();
  return deals || [];
}

/**
 * Batched store-logo lookup, decoupled from price discovery so both Tier 1
 * (Lattice) and Tier 2/3 (Serper) results can get a logo. Returns a map keyed
 * by the exact names passed in.
 */
export async function fetchStoreLogos(storeNames: string[]): Promise<Record<string, string | null>> {
  const uniqueNames = Array.from(new Set(storeNames.filter(Boolean)));
  if (uniqueNames.length === 0) return {};

  try {
    const token = await getMarketDiscoverToken();
    const response = await fetch('/api/market/store-logos', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ storeNames: uniqueNames }),
    });
    if (!response.ok) return {};
    const { logos } = await response.json();
    return logos || {};
  } catch (err) {
    console.error('[marketDiscovery] Store logo lookup failed:', err);
    return {};
  }
}

/**
 * Fallback product-photo lookup for results with no saved image — Basket IQ's
 * personal-history and Lattice tiers never carry one the way a receipt scan
 * or a Serper Shopping listing does. Returns a map keyed by the exact product
 * names passed in.
 */
export async function fetchProductImages(products: Array<{ name: string; brand?: string | null }>): Promise<Record<string, string | null>> {
  const uniqueProducts = Array.from(new Map(products.filter((p) => p.name).map((p) => [p.name, p])).values());
  if (uniqueProducts.length === 0) return {};

  try {
    const token = await getMarketDiscoverToken();
    const response = await fetch('/api/market/product-images', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ products: uniqueProducts }),
    });
    if (!response.ok) return {};
    const { images } = await response.json();
    return images || {};
  } catch (err) {
    console.error('[marketDiscovery] Product image lookup failed:', err);
    return {};
  }
}

/**
 * Same lookup as fetchProductImages, but keyed back to caller-supplied item ids instead of
 * product names — the shape every "attach a stock photo to a just-extracted item" call site
 * actually wants, since names alone aren't safe merge keys once multiple items share a name.
 * Every item-creation surface (basket scans, manual add, CartAssistant, bulk import) should
 * route through this one helper rather than re-deriving the name/brand → image request itself.
 */
export async function fetchStockImagesById(
  items: Array<{ id: string; name: string; brand?: string | null }>
): Promise<Record<string, string>> {
  const images = await fetchProductImages(items.map((i) => ({ name: i.name, brand: i.brand })));
  const byId: Record<string, string> = {};
  for (const item of items) {
    const url = images[item.name];
    if (url) byId[item.id] = url;
  }
  return byId;
}

// Mirrors the Haversine formula used server-side in get_lattice_density /
// get_global_lattice_prices (supabase/migrations/20260407000000_fix_lattice_density_acos.sql)
// so a personal-history store's distance is computed identically to a Lattice one.
export function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const arg = Math.sin(toRad(lat1)) * Math.sin(toRad(lat2)) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lng1) - toRad(lng2));
  return 3959 * Math.acos(Math.min(1, Math.max(-1, arg)));
}

/**
 * Hierarchy of Truth: Tier 1 (Geospatial Lattice receipt scans) always overrides
 * Tier 2/3 (Serper regional chain estimates) for the same physical store.
 */
export const resolveMarketDeals = async ({
  supabase,
  query,
  itemPrice,
  itemSize,
  itemUnit,
  userLat,
  userLng,
  userAddress,
  radiusMiles = CALIBRATION_DEFAULTS.latticeRadiusMiles,
  lookbackHours = CALIBRATION_DEFAULTS.latticeHorizonHours,
}: ResolveMarketDealsParams): Promise<GroundedDeal[]> => {
  const lattice = await getGlobalLatticePrices(
    supabase,
    '',
    userLat ?? undefined,
    userLng ?? undefined,
    radiusMiles,
    lookbackHours,
    null,
    query
  );

  // query is item.name — a name CartLens itself generated via formatDisplayName(),
  // so destructureName() (which reverses that exact "Entity (Details)" format) is
  // the correct tool here, unlike for raw third-party retailer titles.
  const queryParts = destructureName(query);

  const verifiedDeals: GroundedDeal[] = (lattice || [])
    .filter((row: any) => row.price != null)
    .map((row: any) => ({
      name: query,
      brand: queryParts.brand,
      productName: queryParts.product,
      variety: queryParts.variety,
      category: 'Other',
      size: queryParts.size,
      unit: queryParts.unit,
      upc: null,
      gtin: null,
      plu: null,
      imageUrl: null,
      sourceUrl: null,
      storeName: row.store_name || 'Local Store',
      storeAddress: row.store_address || '',
      price: Number(row.price),
      distance: row.distance_miles != null ? Number(row.distance_miles) : null,
      pricePerUnit: computePricePerUnit(Number(row.price), queryParts.size),
      tier: 'verified' as const,
      lat: row.latitude != null ? Number(row.latitude) : null,
      lng: row.longitude != null ? Number(row.longitude) : null,
    }));

  let estimateDeals: GroundedDeal[] = [];
  // storeName -> best logo-lookup key (chainName when available), for the final logo pass.
  const logoKeyByStoreName = new Map<string, string>();

  if (
    verifiedDeals.length < MIN_LOCAL_LATTICE_DENSITY &&
    typeof userLat === 'number' &&
    typeof userLng === 'number'
  ) {
    try {
      const rawDeals = await callMarketDiscoverApi(query, userLat, userLng, userAddress);
      // Serper titles are free-form retailer text ("Tide PODS Free & Gentle HE
      // Laundry Detergent Pacs") — destructureName only reverses CartLens's own
      // "Entity (Details)" display format, so it can't parse these. Structure
      // them properly in one batched call instead.
      const parsedTitles = await structureProductTitles(rawDeals.map((d) => d.title || query));

      estimateDeals = rawDeals
        .map((deal, i) => ({ deal, parsed: parsedTitles[i] }))
        .filter(({ deal, parsed }) =>
          isPlausibleMatch(deal.price, parsed.size, parsed.unit, itemPrice, itemSize, itemUnit)
        )
        .map(({ deal, parsed }) => {
          logoKeyByStoreName.set(deal.storeName, deal.chainName || deal.storeName);
          return {
            name: formatDisplayName({
              brand: parsed.brand,
              product: parsed.product,
              variety: parsed.variety,
              size: parsed.size,
              unit: parsed.unit,
            }),
            brand: parsed.brand,
            productName: parsed.product,
            variety: parsed.variety,
            category: 'Other',
            size: parsed.size,
            unit: parsed.unit,
            upc: null,
            gtin: null,
            plu: null,
            imageUrl: deal.imageUrl || null,
            sourceUrl: deal.sourceUrl || null,
            storeName: deal.storeName,
            storeAddress: deal.storeAddress || '',
            price: deal.price,
            distance: deal.distanceMiles ?? null,
            pricePerUnit: computePricePerUnit(deal.price, parsed.size),
            tier: 'estimate' as const,
            lat: deal.branchLat ?? null,
            lng: deal.branchLng ?? null,
          };
        });
    } catch (err) {
      console.error('[marketDiscovery] Serper discovery failed:', err);
    }
  }

  // Step D (Override Resolution): a Tier 1 verified price at a store always wins,
  // so drop any Tier 2/3 estimate that resolved to the same physical building.
  const verifiedStoreKeys = new Set(
    verifiedDeals.map((d) => `${d.storeName}|${d.storeAddress}`.toLowerCase())
  );
  const filteredEstimates = estimateDeals.filter(
    (d) => !verifiedStoreKeys.has(`${d.storeName}|${d.storeAddress}`.toLowerCase())
  );

  // Rank by price-per-unit whenever a deal's unit matches the scanned item's own
  // unit — that's the fair "which is actually cheaper" comparison. Deals with no
  // unit, or a different unit (no cross-unit conversion yet), fall back to raw
  // price and sort after the directly-comparable ones rather than mixing the two
  // incomparable metrics into one ranking.
  const normalizedItemUnit = itemUnit?.trim().toLowerCase() || null;
  const isComparable = (d: GroundedDeal) =>
    d.pricePerUnit != null && normalizedItemUnit != null && d.unit?.trim().toLowerCase() === normalizedItemUnit;

  const allDeals = [...verifiedDeals, ...filteredEstimates];
  const comparable = allDeals.filter(isComparable).sort((a, b) => a.pricePerUnit! - b.pricePerUnit!);
  const rest = allDeals.filter((d) => !isComparable(d)).sort((a, b) => a.price - b.price);

  const finalDeals = [...comparable, ...rest].slice(0, 5);

  if (finalDeals.length > 0) {
    const logoKeys = finalDeals.map((d) => logoKeyByStoreName.get(d.storeName) || d.storeName);
    const logos = await fetchStoreLogos(logoKeys);
    finalDeals.forEach((d) => {
      const key = logoKeyByStoreName.get(d.storeName) || d.storeName;
      d.storeLogoUrl = logos[key] ?? null;
    });
  }

  return finalDeals;
};

interface DiscoverProductsParams {
  query: string;
  userLat?: number | null;
  userLng?: number | null;
  userAddress?: string | null;
}

// Groups raw Serper offers into distinct products so genuinely different pack
// sizes/variants never get merged into one product's "compare across stores" list.
function productGroupKey(parsed: StructuredProductName): string {
  return [parsed.brand, parsed.product, parsed.variety, parsed.size, parsed.unit]
    .map((v) => (v == null ? '' : String(v).trim().toLowerCase()))
    .join('|');
}

/**
 * Open-ended multi-product discovery for a free-text search (e.g. "milk" returning
 * several distinct milk products, each with its own local store price list).
 * Serper-only (Tier 2/3) — there is no single known item to check against the
 * Geospatial Lattice the way there is in resolveMarketDeals().
 */
export const discoverProducts = async ({
  query,
  userLat,
  userLng,
  userAddress,
}: DiscoverProductsParams): Promise<GroundedProduct[]> => {
  if (typeof userLat !== 'number' || typeof userLng !== 'number') return [];

  const rawDeals = await callMarketDiscoverApi(query, userLat, userLng, userAddress, true);
  if (rawDeals.length === 0) return [];

  const parsedTitles = await structureProductTitles(rawDeals.map((d) => d.title || query));

  const groups = new Map<string, { parsed: StructuredProductName; deals: RawMarketDeal[] }>();
  rawDeals.forEach((deal, i) => {
    const parsed = parsedTitles[i];
    const key = productGroupKey(parsed);
    if (!groups.has(key)) {
      groups.set(key, { parsed, deals: [] });
    }
    groups.get(key)!.deals.push(deal);
  });

  // Google Shopping routinely returns multiple near-duplicate listings for
  // the same retailer (different aggregator/product-page URLs, same item) —
  // "Branch Binding" in /api/market/discover then correctly resolves each of
  // those listings to the SAME single nearest physical branch, since it's
  // genuinely the same store, not a location-matching error. Left undeduped,
  // that store would appear twice in one product's localPrices. Dedupe by
  // physical branch (lat/lng when available, since two listings bound to the
  // same branch always carry identical coordinates — falls back to the
  // formatted store name when a branch has no coordinates) before both the
  // "more store options" ranking below and the final localPrices list, so
  // duplicate listings don't inflate a product's apparent store count either.
  const dedupeDealsByStore = (deals: RawMarketDeal[]): RawMarketDeal[] => {
    const seen = new Map<string, RawMarketDeal>();
    // Deals are pushed in Serper's original order; sort by price first so the
    // cheapest duplicate listing at a given store is the one kept.
    [...deals].sort((a, b) => a.price - b.price).forEach((deal) => {
      const key = deal.branchLat != null && deal.branchLng != null
        ? `${deal.branchLat},${deal.branchLng}`
        : getFormattedStoreDisplayName({ name: deal.storeName, address: deal.storeAddress }).toLowerCase();
      if (!seen.has(key)) seen.set(key, deal);
    });
    return Array.from(seen.values());
  };

  // Products with more verified store options surface first — a richer comparison
  // is more useful than a single-store result, then break ties on cheapest price.
  const topGroups = Array.from(groups.values())
    .map((group) => ({ ...group, deals: dedupeDealsByStore(group.deals).sort((a, b) => a.price - b.price) }))
    .sort(
      (a, b) => b.deals.length - a.deals.length || a.deals[0].price - b.deals[0].price
    )
    .slice(0, 5);

  const logos = await fetchStoreLogos(
    topGroups.flatMap((g) => g.deals.map((d) => d.chainName || d.storeName))
  );

  return topGroups.map(({ parsed, deals }) => {
    const withImage = deals.find((d) => d.imageUrl);
    const withSource = deals.find((d) => d.sourceUrl);

    return {
      name: formatDisplayName({
        brand: parsed.brand,
        product: parsed.product,
        variety: parsed.variety,
        size: parsed.size,
        unit: parsed.unit,
      }),
      brand: parsed.brand,
      productName: parsed.product,
      variety: parsed.variety,
      category: 'Other',
      size: parsed.size,
      unit: parsed.unit,
      upc: null,
      gtin: null,
      plu: null,
      imageUrl: withImage?.imageUrl || null,
      sourceUrl: withSource?.sourceUrl || undefined,
      localPrices: deals.map((d) => ({
        storeName: getFormattedStoreDisplayName({ name: d.storeName, address: d.storeAddress }),
        price: d.price,
        sourceUrl: d.sourceUrl || undefined,
        distance: d.distanceMiles,
        storeLogoUrl: logos[d.chainName || d.storeName] ?? null,
        storeAddress: d.storeAddress || undefined,
        lat: d.branchLat ?? null,
        lng: d.branchLng ?? null,
        // All entries in this group share the same size/unit by construction
        // (productGroupKey includes both), so this is always a fair comparison
        // within the list — no cross-unit conversion needed here.
        pricePerUnit: computePricePerUnit(d.price, parsed.size),
      })),
    };
  });
};

export interface MarketBasketAddition {
  name: string;
  price: number;
  category: string;
  imageUrl: string | null;
  unit: string | null;
  brand: string | null;
  productName: string | null;
  variety: string | null;
  size: number | null;
  upc: string | null;
  gtin: string | null;
  plu: string | null;
  metadata: Record<string, any>;
}

/**
 * Shared by BasketProductSearch.tsx and the CartAssistant's add_market_product_to_basket
 * tool so both "add this discovered product to my basket" entry points enrich and shape
 * the item identically. Enriches missing category/UPC/GTIN/size/unit at add-time only,
 * for the one item actually picked, rather than every discovered candidate up front.
 */
export async function resolveMarketProductForBasket(
  product: GroundedProduct,
  option: { storeName: string; price: number; sourceUrl?: string; distance?: number | null },
  existingCategoryNames: string[]
): Promise<MarketBasketAddition> {
  let finalSize: number | null =
    typeof product.size === 'number' ? product.size : (typeof product.size === 'string' ? (parseFloat(product.size) || null) : null);
  let finalUnit = product.unit || null;
  let finalUpc = product.upc || null;
  let finalGtin = product.gtin || null;
  let msrp: number | null = null;

  // Resolve alternative cheaper store options for the Leakage Protocol
  const cheaperDeals = product.localPrices.filter((p) => p.price < option.price);
  const bestDeal = cheaperDeals.length > 0 ? cheaperDeals[0] : null;

  let category = product.category;
  const needsCategory = !category || category === 'Other';
  const needsIdentity = !finalUpc || !finalGtin || !finalSize || !finalUnit;

  if (needsCategory || needsIdentity) {
    if (needsCategory) {
      category = (await suggestCategory(product.name, existingCategoryNames)) || 'Other';
    }
    if (needsIdentity) {
      const grounded = await groundMissingIdentity(
        { brand: product.brand, product: product.productName, variety: product.variety, size: finalSize, unit: finalUnit },
        finalUpc
      );
      finalUpc = finalUpc || grounded.upc;
      finalGtin = finalGtin || grounded.gtin;
      finalSize = finalSize || (typeof grounded.size === 'number' ? grounded.size : null);
      finalUnit = finalUnit || grounded.unit;
      msrp = grounded.msrp;
    }
  }

  return {
    name: product.name,
    price: option.price,
    category,
    imageUrl: product.imageUrl || null,
    unit: finalUnit,
    brand: product.brand,
    productName: product.productName,
    variety: product.variety,
    size: finalSize,
    upc: finalUpc,
    gtin: finalGtin,
    plu: product.plu || null,
    metadata: {
      source: option.sourceUrl || product.sourceUrl || null,
      is_grounded_match: true,
      ...(msrp != null ? { msrp } : {}),
      betterDeal: bestDeal
        ? {
            price: bestDeal.price,
            storeName: bestDeal.storeName,
            distance: bestDeal.distance || null,
          }
        : null,
    },
  };
}

export interface LatticeSearchResult {
  productName: string;
  canonicalId: string;
  price: number;
  storeName: string;
  storeAddress?: string | null;
  lat?: number | null;
  lng?: number | null;
  distanceMiles?: number | null;
  date: string;
  size?: number | string | null;
  unit?: string | null;
  pricePerUnit?: number | null;
  // false = donated (not tied to the contributor's own personal trip) —
  // lets callers split "lattice" (someone else's shared trip) from "donated".
  isPersonalTrip?: boolean;
}

/**
 * Free-text canonical product search across the Lattice — extracted from the
 * CartAssistant search_global_lattice tool so both it and Basket IQ's
 * cross-tier search (searchAllTiers) share one implementation.
 */
export async function searchLatticeByName(
  supabase: SupabaseClient,
  query: string,
  opts: { lat?: number | null; lng?: number | null; radiusMiles: number; lookbackHours: number }
): Promise<LatticeSearchResult[]> {
  const { data: canonicals, error } = await supabase
    .from('product_canonical')
    .select('id, slug, name')
    .ilike('name', `%${query}%`)
    .limit(10);

  if (error || !canonicals || canonicals.length === 0) return [];

  const slugs = canonicals.map((c) => c.slug);
  const slugToName = Object.fromEntries(canonicals.map((c) => [c.slug, c.name]));

  const prices = await getBatchGlobalLatticePrices(
    supabase,
    slugs,
    opts.lat ?? undefined,
    opts.lng ?? undefined,
    opts.radiusMiles,
    opts.lookbackHours
  );

  if (!prices || prices.length === 0) return [];

  // Deduplicate: keep only the cheapest price per store branch per product
  // slug. The address is part of the key so two branches of one chain stay
  // separate, each with its own location.
  const seen = new Map<string, any>();
  for (const p of prices) {
    const key = `${p.store_name}||${p.store_address ?? ''}||${p.item_slug}`;
    if (!seen.has(key) || p.price < seen.get(key).price) {
      seen.set(key, p);
    }
  }

  return Array.from(seen.values())
    .sort((a, b) => {
      // Rank by price-per-unit when both sides share a unit — "cheapest"
      // should mean best value, not just lowest total for a smaller pack.
      const aPPU = computePricePerUnit(a.price, a.size);
      const bPPU = computePricePerUnit(b.price, b.size);
      const sameUnit = a.unit && b.unit && String(a.unit).trim().toLowerCase() === String(b.unit).trim().toLowerCase();
      if (sameUnit && aPPU != null && bPPU != null) return aPPU - bPPU;
      return a.price - b.price;
    })
    .map((p) => {
      // product_canonical.name is raw text captured once at row-creation time
      // and never re-normalized — unlike Serper-sourced names, which always
      // go through formatDisplayName(). Round-trip through destructureName()
      // so Lattice results render in the same "Entity (Details)" nomenclature
      // as every other tier, using the RPC's own size/unit (structured
      // columns) over whatever destructureName can parse out of the raw text.
      const rawName = slugToName[p.item_slug] || p.item_slug;
      const parsed = destructureName(rawName);
      const productName = formatDisplayName({
        brand: parsed.brand,
        product: parsed.product,
        variety: parsed.variety,
        size: p.size ?? parsed.size,
        unit: p.unit ?? parsed.unit,
      });
      return {
        productName,
        canonicalId: p.item_slug,
        price: p.price,
        storeName: p.store_name,
        storeAddress: p.store_address || undefined,
        lat: p.latitude != null ? Number(p.latitude) : undefined,
        lng: p.longitude != null ? Number(p.longitude) : undefined,
        distanceMiles: p.distance_miles != null ? Number(p.distance_miles) : undefined,
        size: p.size ?? undefined,
        unit: p.unit ?? undefined,
        pricePerUnit: computePricePerUnit(p.price, p.size) ?? undefined,
        date: p.price_date,
        isPersonalTrip: p.is_personal_trip !== false,
      };
    });
}

export type SearchCategoryTier = 'personal' | 'lattice' | 'donated' | 'estimate';

export interface CategoryLocalPrice {
  storeName: string;
  price: number;
  storeAddress?: string | null;
  distance?: number | null;
  storeLogoUrl?: string | null;
  pricePerUnit?: number | null;
  sourceUrl?: string | null;
  lat?: number | null;
  lng?: number | null;
  // 'organic' = someone else's personal trip, shared with the Lattice.
  // 'field_collected' = a donation — not tied to any personal trip, submitted
  // specifically to contribute to the Lattice (shopping_history.is_personal_trip=false).
  origin?: 'organic' | 'field_collected';
}

export interface CategoryProductGroup {
  name: string;
  brand?: string | null;
  category?: string;
  size?: number | string | null;
  unit?: string | null;
  imageUrl?: string | null;
  upc?: string | null;
  localPrices: CategoryLocalPrice[];
}

export interface CategorizedSearchResults {
  personal: CategoryProductGroup[];
  lattice: CategoryProductGroup[];
  // Contributions where is_personal_trip=false — donated to the Lattice, not
  // tied to the contributor's own shopping trip. Pending admin/merchant review
  // (lattice_status) before they surface here at all.
  donated: CategoryProductGroup[];
  estimate: CategoryProductGroup[];
}

export interface SearchAllTiersParams {
  supabase: SupabaseClient;
  query: string;
  // Caller-supplied (e.g. ComparisonView's itemMetadata/itemData) rather than
  // this module reaching into page-local state.
  personalIndex: Record<string, { primaryName: string; slug: string; stores: Record<string, Array<{ price: number; date: string; address?: string | null; lat?: number | null; lng?: number | null; imageUrl?: string | null }>> }>;
  userLat?: number | null;
  userLng?: number | null;
  userAddress?: string | null;
  radiusMiles?: number;
  lookbackHours?: number;
}

/**
 * Cross-tier, grouped-by-product search for Basket IQ: personal purchase
 * history, the Lattice (other shoppers' receipts), donated/field-collected
 * receipts (no data source yet, always empty), and Live Market Discovery
 * (Serper) as the final tier. Each category is returned separately — rather
 * than merged into one list — so the UI can render priority-ordered,
 * independently filterable sections. A product+store already covered by a
 * higher-priority tier is not duplicated by a lower one.
 */
export async function searchAllTiers(params: SearchAllTiersParams): Promise<CategorizedSearchResults> {
  const { supabase, query, personalIndex, userLat, userLng, userAddress, radiusMiles = CALIBRATION_DEFAULTS.latticeRadiusMiles, lookbackHours = CALIBRATION_DEFAULTS.latticeHorizonHours } = params;
  const q = query.trim().toLowerCase();
  if (!q) return { personal: [], lattice: [], donated: [], estimate: [] };

  // Tier 1: personal history
  const personalEntries: Array<{ key: string; primaryName: string; storeName: string; latest: { price: number; address?: string | null; lat?: number | null; lng?: number | null; imageUrl?: string | null } }> = [];
  Object.values(personalIndex).forEach((entry) => {
    if (!entry.primaryName.toLowerCase().includes(q)) return;
    Object.entries(entry.stores).forEach(([storeName, priceHistory]) => {
      if (!priceHistory || priceHistory.length === 0) return;
      const latest = [...priceHistory].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )[0];
      personalEntries.push({ key: entry.primaryName.toLowerCase(), primaryName: entry.primaryName, storeName, latest });
    });
  });
  const personalLogos = await fetchStoreLogos(personalEntries.map((e) => e.storeName));
  const personalGroups = new Map<string, CategoryProductGroup>();
  personalEntries.forEach(({ key, primaryName, storeName, latest }) => {
    if (!personalGroups.has(key)) {
      personalGroups.set(key, { name: primaryName, localPrices: [] });
    }
    const group = personalGroups.get(key)!;
    if (!group.imageUrl && latest.imageUrl) group.imageUrl = latest.imageUrl;
    const distance = typeof userLat === 'number' && typeof userLng === 'number'
      && typeof latest.lat === 'number' && typeof latest.lng === 'number'
      ? haversineMiles(userLat, userLng, latest.lat, latest.lng)
      : null;
    group.localPrices.push({
      storeName: getFormattedStoreDisplayName({ name: storeName, address: latest.address }),
      price: latest.price,
      storeAddress: latest.address,
      distance,
      storeLogoUrl: personalLogos[storeName] ?? null,
      lat: latest.lat,
      lng: latest.lng,
    });
  });
  const personal = Array.from(personalGroups.values());
  const coveredKeys = new Set<string>();
  personal.forEach((g) => g.localPrices.forEach((lp) => coveredKeys.add(`${g.name.toLowerCase()}|${lp.storeName.toLowerCase()}`)));

  // Tier 2: Lattice (other shoppers' personal trips, shared) + Tier 3: Donated
  // (not tied to anyone's personal trip, contributed specifically to the
  // Lattice) — one query, split by is_personal_trip on each row.
  let lattice: CategoryProductGroup[] = [];
  let donated: CategoryProductGroup[] = [];
  try {
    const latticeResults = await searchLatticeByName(supabase, query, { lat: userLat, lng: userLng, radiusMiles, lookbackHours });
    const latticeLogos = await fetchStoreLogos(latticeResults.map((r) => r.storeName));
    const latticeGroups = new Map<string, CategoryProductGroup>();
    const donatedGroups = new Map<string, CategoryProductGroup>();
    latticeResults.forEach((r) => {
      const key = `${r.productName.toLowerCase()}|${r.storeName.toLowerCase()}`;
      if (coveredKeys.has(key)) return;
      const isDonated = r.isPersonalTrip === false;
      const groups = isDonated ? donatedGroups : latticeGroups;
      const groupKey = r.canonicalId || r.productName.toLowerCase();
      if (!groups.has(groupKey)) {
        groups.set(groupKey, { name: r.productName, size: r.size, unit: r.unit, localPrices: [] });
      }
      groups.get(groupKey)!.localPrices.push({
        storeName: getFormattedStoreDisplayName({ name: r.storeName, address: r.storeAddress }),
        price: r.price,
        storeAddress: r.storeAddress,
        distance: r.distanceMiles,
        storeLogoUrl: latticeLogos[r.storeName] ?? null,
        pricePerUnit: r.pricePerUnit,
        origin: isDonated ? 'field_collected' : 'organic',
        lat: r.lat,
        lng: r.lng,
      });
      coveredKeys.add(key);
    });
    lattice = Array.from(latticeGroups.values());
    donated = Array.from(donatedGroups.values());
  } catch (err) {
    console.error('[marketDiscovery] searchAllTiers: Lattice search failed:', err);
  }

  // Tier 4: Online (Live Market Discovery / Serper)
  let estimate: CategoryProductGroup[] = [];
  if (typeof userLat === 'number' && typeof userLng === 'number') {
    try {
      const products = await discoverProducts({ query, userLat, userLng, userAddress });
      estimate = products
        .map((product) => ({
          name: product.name,
          brand: product.brand,
          category: product.category,
          size: product.size,
          unit: product.unit,
          imageUrl: product.imageUrl,
          upc: product.upc,
          localPrices: product.localPrices
            .filter((option) => !coveredKeys.has(`${product.name.toLowerCase()}|${option.storeName.toLowerCase()}`))
            .map((option) => ({
              // Already formatted by discoverProducts() — don't re-run through
              // getFormattedStoreDisplayName, which would re-split an already-joined
              // "Name, Street" string on its own comma.
              storeName: option.storeName,
              price: option.price,
              distance: option.distance,
              storeLogoUrl: option.storeLogoUrl,
              pricePerUnit: option.pricePerUnit,
              sourceUrl: option.sourceUrl || product.sourceUrl,
              storeAddress: option.storeAddress,
              lat: option.lat,
              lng: option.lng,
            })),
        }))
        .filter((group) => group.localPrices.length > 0);
    } catch (err) {
      console.error('[marketDiscovery] searchAllTiers: Live Market Discovery failed:', err);
    }
  }

  // Personal-history and Lattice results have no reliable image source of
  // their own (a receipt scan doesn't always capture one, and Lattice rows —
  // other shoppers' price observations — never carry one at all). Fall back
  // to a Serper product-photo search for whichever groups still lack one,
  // rather than leaving them blank like Online/Serper results never are.
  const needsImage = [...personal, ...lattice].filter((g) => !g.imageUrl);
  if (needsImage.length > 0) {
    const images = await fetchProductImages(needsImage.map((g) => ({ name: g.name, brand: g.brand })));
    needsImage.forEach((g) => { g.imageUrl = images[g.name] ?? null; });
  }

  return { personal, lattice, donated, estimate };
}
